import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getReviewManageSingleRowTabularDataApi } from 'api/analytics/review';
import { sendRequest } from 'api/sendRequest';
import { useReviewManageSidepanelData } from 'reducers/reviewManageSidepanelSlice';

import { endpoints } from 'constants/endpoints';
import { getRabbitColorFromKey } from 'constants/helper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { setReviewManageRowDefaultValues } from '../dataHooks';

// import { useReviewManagePanelQueryParams } from './sidepanelStateHooks';

const getReviewManageSidepanelInfoApi = ({ reviewCycleId, employeeId }) => {
  const url = endpoints.performanceReview.reviewManage.panel;
  return sendRequest({
    url,
    credentials: 'include',
    queryParams: { employeeId, reviewCycleId },
  });
};

const getGridDefinitionOldFormat = (gridDefinition) => {
  if (!gridDefinition) return [];
  return Object.values(gridDefinition).map((row) =>
    row.map(({ color, label, scoreRange }) => ({
      color: getRabbitColorFromKey(color),
      label,
      scoreRange,
    }))
  );
};

export const getSimpleAndAPIScoreMap = ({ scoreMap, answerType }) => {
  const simpleScoreMap = {};
  const apiScoreMap = scoreMap ?? {};
  Object.keys(scoreMap ?? {}).forEach((key) => {
    simpleScoreMap[key] = scoreMap?.[key]?.ratingText ?? '';

    // for the case where rating is coming as null from the backend
    if (scoreMap?.[key] === null) {
      apiScoreMap[key] = {
        ratingText: '',
        ratingType: answerType ?? 'NUMBER_RATING',
        isCalibrated: false,
      };
    }
  });
  return [simpleScoreMap, apiScoreMap];
};

export const useGetReviewManageSidepanelInfo = ({ reviewCycleId, employeeId, enabled = true } = {}) => {
  // const { reviewManageEmpId, reviewManageReviewId } = useReviewManagePanelQueryParams();
  const { selectedReviewCycle, empId } = useReviewManageSidepanelData();
  const reviewManageEmpId = empId;
  const reviewManageReviewId = selectedReviewCycle?.uuid;

  const rcId = reviewManageReviewId || reviewCycleId;
  const rcEmpId = reviewManageEmpId || employeeId;

  return useQuery(
    [REACT_QUERY_KEYS.reviewManage.sidepanel, rcId, rcEmpId],
    () =>
      getReviewManageSidepanelInfoApi({ reviewCycleId: rcId, employeeId: rcEmpId }).then((res) => {
        const [calibrationScoreMap, calibrationApiScoreMap] = res?.entity?.metadata?.isCalibrationByLabel
          ? getSimpleAndAPIScoreMap({
              scoreMap: res.entity.scoreMap,
              answerType: 'DROP_DOWN',
            })
          : getSimpleAndAPIScoreMap({
              scoreMap: res.entity.scoreMapNumeric,
              answerType: 'NUMBER_RATING',
            });

        const [finalScoreMap] = getSimpleAndAPIScoreMap({
          scoreMap: res.entity.scoreMap,
          answerType: res.entity.metadata?.calibrationScoreSettings?.answerType,
        });

        const [finalScoreMapNumeric] = getSimpleAndAPIScoreMap({
          scoreMap: res.entity.scoreMapNumeric,
          answerType: 'NUMBER_RATING',
        });

        return {
          ...res.entity,
          finalScoreMap,
          finalScoreMapNumeric,
          calibrationScoreMap,
          calibrationApiScoreMap,
          metadata: {
            ...res.entity.metadata,
            gridDefinition: getGridDefinitionOldFormat(res.entity?.metadata?.gridDefinition),
          },
        };
      }),
    {
      enabled: !!rcId && !!rcEmpId && enabled,
      staleTime: 60000,
    }
  );
};

// todo: vikalp1
export const useInvalidateReviewManageSidepanelData = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewManage.sidepanel]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewManage.providers]);
    // this key is same for both calibration view and review manage panel
    queryClient.invalidateQueries([REACT_QUERY_KEYS.talentPlanning.sidepanelCalibrationHistory]);
  }, [queryClient]);
};

export const useUpdateReviewManageSingleRowData = () => {
  const queryClient = useQueryClient();

  return useCallback(
    ({ reviewCycleId, empId, cohortDetails }) => {
      getReviewManageSingleRowTabularDataApi({ reviewCycleId, empId }).then((res) => {
        queryClient.setQueryData(
          [REACT_QUERY_KEYS.reviewsManage, cohortDetails?.id, reviewCycleId],
          ({ columns, rows }) => {
            const { employeeId, row: newRowData } = res.entity ?? {};
            const newRows = rows.map((row) => {
              if (row.EMPLOYEE.id === employeeId) {
                return setReviewManageRowDefaultValues(newRowData);
              }
              return row;
            });
            return { columns, rows: newRows };
          }
        );
      });
    },
    [queryClient]
  );
};
